import { SortInput } from '@apeiron/common'
import { EMPTY_CURRENCY, useCurrencyContext } from '@apeiron/currency'
import {
  OrbitalTrackFilterInput,
  OrbitalTrackSortBy,
  PUBLIC_RENTAL_GROUP_ID
} from '@apeiron/orbital'
import { StarSuperLite } from '@apeiron/star'
import {
  ApeironBlueButton,
  ApeironPagingDataGrid,
  ApeironPagingDataGridRef,
  MayNull,
  NextLink,
  PagingDatasourceResult
} from '@apeiron/temp'
import { styled, Typography } from '@mui/material'
import OrbitalTrackAttachDialog from '@src/components/orbitalTrack/attach/OrbitalTrackAttachDialog'
import RentalSettingDialog, {
  RentalType
} from '@src/components/orbitalTrack/rentalSetting/common/RentalSettingDialog'
import ApeironBorderButton from '@src/components/share/apeiron/ApeironBorderButton'
import { ROUTE } from '@src/constants/route'
import {
  OrbitalTrackAttachProvider,
  useOrbitalTrackAttachContext
} from '@src/contexts/orbitalTrack/OrbitalTrackAttachContext'
import {
  OrbitalTrackRentalSettingProvider,
  useOrbitalTrackRentalSettingContext
} from '@src/contexts/orbitalTrack/OrbitalTrackRentalSettingContext'
import useCheckOwner from '@src/hooks/apeiron/account/useCheckOwner'
import useGetPublicStarRentals from '@src/hooks/graphql/useGetPublicStarRentals'
import { PublicStarRentalLite } from '@src/types/orbitalTrack'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import queryString from 'query-string'
import * as R from 'ramda'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const DataGrid = styled(ApeironPagingDataGrid)`
  .Custom-Grid {
    border-radius: unset;

    .ag-header {
      color: white;
      font-weight: 300;
      font-size: 16px;
    }
    .ag-header-row {
      background-color: #151615;
    }
    .ag-cell {
      color: #9b9b9b;
      font-size: 16px;
    }
  }
`

const StyledLink = styled(NextLink)`
  text-decoration: underline;
  :hover {
    color: #febe36;
  }
`

const Label = styled(Typography)`
  background-color: #febe36;
  border-radius: 6px;
  color: black;
  padding: 4px 10px;
`

const EditButton = styled(ApeironBorderButton)`
  border: 1px solid #818181;
  font-weight: 300;
  min-width: 148px;
  padding: 5px;
`

const RentButton = styled(ApeironBlueButton)`
  border-radius: 3px;
  font-weight: 300;
  min-width: 148px;
  padding: 5px;
`

const InProvider: FC<Props> = props => {
  const { className, filterInput, sortInput } = props

  const { resetContext, setStarId, setGroupId } = useOrbitalTrackAttachContext()

  const { setOpen: setOpenSettingPopup, open } =
    useOrbitalTrackRentalSettingContext()

  const { getApeironPriceWithUnit } = useCurrencyContext()

  const [openAttachPopup, setOpenAttachPopup] = useState<boolean>(false)

  const { checkStarOwner } = useCheckOwner()

  const { fetch: fetchRentals } = useGetPublicStarRentals()

  const { t } = useTranslation()

  const ref = useRef<ApeironPagingDataGridRef>(null)

  const handleOnFetch = useCallback(
    async (page: number, pageSize: number): Promise<PagingDatasourceResult> => {
      const result = await fetchRentals({
        filterInput,
        sortInput: sortInput.sortBy,
        pageInput: {
          page,
          pageSize
        }
      })

      return {
        data: R.propOr([], 'data', result),
        total: R.pathOr(0, ['pageInfo', 'total'], result)
      }
    },
    [fetchRentals, filterInput, sortInput.sortBy]
  )

  const handleOnOperation = useCallback(
    (starId: string, groupId: string) => {
      resetContext()
      setStarId(starId)
      setGroupId(groupId)
    },
    [resetContext, setGroupId, setStarId]
  )

  const handleOnEdit = useCallback(
    (starId: string, groupId: string) => {
      handleOnOperation(starId, groupId)
      setOpenSettingPopup(true)
    },
    [handleOnOperation, setOpenSettingPopup]
  )

  const handleOnRent = useCallback(
    (starId: string, groupId: string) => {
      handleOnOperation(starId, groupId)
      setOpenAttachPopup(true)
    },
    [handleOnOperation]
  )

  const handleOnRentClose = useCallback(
    (success: boolean) => {
      if (success) {
        if (ref.current) {
          ref.current?.refreshData()
        }
      }

      resetContext()
      setOpenAttachPopup(false)
    },
    [resetContext]
  )

  const handleOnEditClose = useCallback(
    (success: boolean) => {
      if (success) {
        if (ref.current) {
          ref.current?.refreshData()
        }
      }

      setOpenSettingPopup(false)

      resetContext()
    },
    [resetContext, setOpenSettingPopup]
  )

  useEffect(() => {
    if (ref.current) {
      ref.current?.refreshData()
    }
  }, [filterInput, sortInput])

  const columns = useMemo((): ColDef<any, PublicStarRentalLite>[] => {
    return [
      {
        field: 'start',
        headerName: '',
        width: 22
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'star',
        flex: 1.4,
        minWidth: 140,
        headerClass: 'custom-ag-header-center',
        headerName: t('orbit.list.grid.star'),
        valueGetter: 'node.id',
        cellRenderer: (params: ICellRendererParams<PublicStarRentalLite>) => {
          const starId = params?.data?.star.id || ''

          const tier = params?.data?.star.tier.name || ''

          const name = params?.data?.star.name || ''

          const url = queryString.stringifyUrl({
            url: ROUTE.MARKETPLACE_STAR_DETAIL,
            query: { starId }
          })

          return (
            <StyledLink
              href={url}
              target='_blank'
            >{`${tier} - ${name}`}</StyledLink>
          )
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'id',
        flex: 1.2,
        minWidth: 120,
        headerClass: 'custom-ag-header-center',
        headerName: t('orbit.list.grid.star_id'),
        valueGetter: 'node.id',
        cellRenderer: (params: ICellRendererParams<PublicStarRentalLite>) => {
          const starId = params?.data?.star.id || 0

          return starId
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'age',
        flex: 1.2,
        minWidth: 120,
        headerClass: 'custom-ag-header-center',
        headerName: t('orbit.list.grid.age_buff'),
        valueGetter: 'node.id',
        cellRenderer: (params: ICellRendererParams<PublicStarRentalLite>) => {
          const buff = params?.data?.agingBuff || 0

          return buff
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'count',
        flex: 1.5,
        minWidth: 150,
        headerClass: 'custom-ag-header-center',
        headerName: t('orbit.list.grid.count'),
        valueGetter: 'node.id',
        cellRenderer: (params: ICellRendererParams<PublicStarRentalLite>) => {
          const freeTracks = R.pathOr(0, ['data', 'freeTracks'], params)

          const rentTracks = R.pathOr(0, ['data', 'rentTracks'], params)

          return freeTracks + rentTracks
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'duration',
        flex: 1.5,
        minWidth: 150,
        headerClass: 'custom-ag-header-center',
        headerName: t('orbit.list.grid.duration'),
        valueGetter: 'node.id',
        cellRenderer: (params: ICellRendererParams<PublicStarRentalLite>) => {
          const duration = params?.data?.duration

          return t('common.i18n_format.day', { count: duration })
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'price',
        flex: 1.5,
        minWidth: 150,
        headerClass: 'custom-ag-header-center',
        headerName: t('orbit.list.grid.price'),
        valueGetter: 'node.id',
        cellRenderer: (params: ICellRendererParams<PublicStarRentalLite>) => {
          return getApeironPriceWithUnit(
            params?.data?.currencyType,
            params?.data?.rentalFee
          )
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'per_day',
        flex: 1.5,
        minWidth: 150,
        headerClass: 'custom-ag-header-center',
        headerName: t('orbit.list.grid.price_per_day'),
        valueGetter: 'node.id',
        cellRenderer: (params: ICellRendererParams<PublicStarRentalLite>) => {
          const duration: number = R.pathOr(0, ['data', 'duration'], params)

          if (R.isNil(params.data?.currencyType) || duration === 0) {
            return EMPTY_CURRENCY
          }

          const feeBN = BigInt(params.data?.rentalFee || '0')

          const dailyFeeBN = feeBN / BigInt(duration)

          return t('orbit.list.grid.daily_fee', {
            fee: getApeironPriceWithUnit(
              params.data?.currencyType,
              dailyFeeBN.toString()
            )
          })
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'renewable',
        flex: 1.8,
        minWidth: 180,
        headerClass: 'custom-ag-header-center',
        headerName: t('orbit.list.grid.allow_renewal'),
        valueGetter: 'node.id',
        cellRenderer: (params: ICellRendererParams<PublicStarRentalLite>) => {
          return params.data?.renewable ? (
            <Label>{t('orbit.list.grid.renewable')}</Label>
          ) : (
            t('common.no')
          )
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'action',
        headerClass: 'custom-ag-header-center',
        headerName: t('orbit.list.grid.action'),
        pinned: 'right',
        valueGetter: 'node.id',
        cellRenderer: (params: ICellRendererParams<PublicStarRentalLite>) => {
          const star = R.pathOr(
            null,
            ['data', 'star'],
            params
          ) as MayNull<StarSuperLite>

          if (R.isNil(star)) {
            return <></>
          }

          const starId = R.propOr('', 'id', star) as string

          const groupId = PUBLIC_RENTAL_GROUP_ID

          const isOwner = checkStarOwner(star)

          if (isOwner) {
            return (
              <EditButton
                disabled={star?.sale}
                disableReason={t('orbit.block_on_star_listing')}
                onClick={() => {
                  handleOnEdit(starId, groupId)
                }}
              >
                {t('common.button.edit')}
              </EditButton>
            )
          }

          return (
            <RentButton
              disabled={(params?.data?.freeTracks || 0) <= 0}
              disableReason={t('orbit.block_on_no_track')}
              onClick={() => {
                handleOnRent(starId, groupId)
              }}
            >
              {t('orbit.list.grid.rent')}
            </RentButton>
          )
        }
      },
      {
        field: 'end',
        headerName: '',
        width: 5
      }
    ]
  }, [checkStarOwner, getApeironPriceWithUnit, handleOnEdit, handleOnRent, t])

  return (
    <>
      <DataGrid
        className={className}
        columnDefs={columns}
        domLayout='autoHeight'
        gridRef={ref}
        headerHeight={60}
        pageSize={20}
        rowHeight={60}
        fetch={handleOnFetch}
      />
      <OrbitalTrackAttachDialog
        isOpened={openAttachPopup}
        onClose={handleOnRentClose}
      />
      <RentalSettingDialog
        open={open}
        rentalType={RentalType.PUBLIC}
        groupId={PUBLIC_RENTAL_GROUP_ID}
        onClose={handleOnEditClose}
      />
    </>
  )
}

const PublicOrbitalTrackDataGrid: FC<Props> = props => {
  return (
    <OrbitalTrackAttachProvider>
      <OrbitalTrackRentalSettingProvider>
        <InProvider {...props} />
      </OrbitalTrackRentalSettingProvider>
    </OrbitalTrackAttachProvider>
  )
}

type Props = {
  className?: string
  filterInput: OrbitalTrackFilterInput
  sortInput: SortInput<OrbitalTrackSortBy>
}

export default PublicOrbitalTrackDataGrid
